<template>
  <modal
    elem-id="contas"
    :show="isVisible"
    :title="modalTitle"
    :close-label="t('Cancelar')"
    :save-label="t('Salvar')"
    :save-fn="handleSave"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible">
      <contas-form
        ref="formRef"
        :isEditMode="isEditMode"
        :contaId="currentContaId ?? undefined">
      </contas-form>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import Modal from '../../ui/Modal.vue';
import ContasForm from './ContasForm.vue';

type ContaId = number;

interface ModalOpenPayload {
  id?: ContaId;
  action?: string;
}

interface FormInstance {
  loadForEdit: () => Promise<void>;
  saveAccount: () => Promise<boolean>;
  resetForm: () => void;
}

const { t } = useI18n();

const isVisible = ref(false);
const isEditMode = ref(false);
const currentContaId = ref<ContaId | null>(null);
const formRef = ref<FormInstance | null>(null);

const modalTitle = computed(() => {
  return isEditMode.value ? t('Editar Conta') : t('Nova Conta');
});

const openForNew = (): void => {
  log('ContasModal', 'openForNew', 'Opening for new account');
  isVisible.value = true;
  isEditMode.value = false;
  currentContaId.value = null;
};

const openForEdit = async (contaId: ContaId): Promise<void> => {
  log('ContasModal', 'openForEdit', 'Opening for edit', contaId);
  isVisible.value = true;
  isEditMode.value = true;
  currentContaId.value = contaId;

  await nextTick();

  if (formRef.value) {
    await formRef.value.loadForEdit();
  }
};

const closeModal = (): void => {
  log('ContasModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  currentContaId.value = null;

  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const handleSave = async (): Promise<void> => {
  log('ContasModal', 'handleSave', 'Saving account');

  if (formRef.value) {
    const success = await formRef.value.saveAccount();
    if (success) {
      closeModal();
      eventBus.emit('contas.saved');
    }
  }
};

// Standard modal open handler (for modal.contas.open event)
const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('ContasModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.id) {
    // Edit mode
    await openForEdit(data.id);
  } else {
    // New mode
    openForNew();
  }
};

// Lifecycle - Event listeners
onMounted(() => {
  log('ContasModal', 'onMounted', 'Component mounted, setting up event listeners');

  // Standard modal events (preferred pattern)
  eventBus.on('modal.contas.open', handleModalOpen);
  eventBus.on('modal.contas.close', closeModal);
});

onBeforeUnmount(() => {
  log('ContasModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');

  // Remove standard modal events
  eventBus.off('modal.contas.open', handleModalOpen);
  eventBus.off('modal.contas.close', closeModal);
});

// Expose methods (legacy support)
defineExpose({
  openForNew,
  openForEdit,
  closeModal
});
</script>